import { KEYS } from 'game/constants/SceneConstants';
import { placeholderScenarioData } from 'game/constants/PlaceholderCmsData';

import {
  SCENARIO_REQUIREMENTS_METRICS,
  SCENARIO_REQUIREMENTS_TYPES,
  OPERATOR_TYPES,
} from 'game/constants/Constants';

export class ScenarioModel {
  constructor({ game, data }) {
    this._scoresModel = game._scores;
    this._game = game;

    this._scenarioData = [...(data?.scenarios || placeholderScenarioData)];

    this._playedScenarioIds = [];
    this._linkedScenarioIds = [];
    this._scenarioIds = [];
    this._chosenOutcomes = [];

    this._currentScenarioPlayersChoice = '';

    this._currentScenario = {};

    this._currentDocuments = {};
    this._readDocuments = [];

    this._outcomes = {};

    this.load();
  }

  load() {
    // create an array list of scenario ids from the scenario data
    this._scenarioData.forEach((scenario) => {
      this._scenarioIds.push(scenario.id);
    });
  }

  // -------------------------------------------
  // Getter / Setter
  // -------------------------------------------

  get getCurrentOutcomes() {
    return this._outcomes;
  }

  get getNegotiatorSprite() {
    return this._chosenOutcomes.length > 0
      ? this._chosenOutcomes[this._chosenOutcomes.length - 1].negotiator_sprite
      : 'neutral';
  }

  get getCurrentScenarioType() {
    return this._currentScenario.data.type;
  }

  get getCurrentDocuments() {
    return this._currentDocuments;
  }

  get getCurrentScenarioTitle() {
    return this._currentScenario.name;
  }

  get getCurrentScenarioId() {
    return this._currentScenario.id;
  }

  get getCurrentScenarioPlayersChoiceData() {
    return {
      result: this._outcomes[this._currentScenarioPlayersChoice].result,
      scores: this._outcomes[this._currentScenarioPlayersChoice].scores,
    };
  }

  // -------------------------------------------
  // Functions to help with scenario selection
  // -------------------------------------------

  getRandomScenario() {
    let scenarioId = null;
    // is it the start of the game and has starting scenarios?

    const startingScenarios =
      this._game._player.currentTimeIndex == 0 ? this.getStartingScenario() : false;

    if (startingScenarios) {
      // get random starting scenario
      scenarioId = this.getRandomScenarioId(startingScenarios);
    } else {
      // get filtered scenarios using getFilteredScenarios
      const filteredScenarios = this.getFilteredScenarios();

      // check if any scenarios are linked to any previously played scenarios
      // if so give these more weight to be chosen
      // else just randomly chose an id
      scenarioId = this.getRandomScenarioId(
        this._linkedScenarioIds.length > 0 && Math.random() > 0.75
          ? this._linkedScenarioIds
          : filteredScenarios
      );
    }
    if (scenarioId) {
      this._currentScenario = this.retrieveScenarioById(scenarioId);
      this._currentDocuments = this._currentScenario?.documents || [];
      this._outcomes = this.parseOutcomesInt(this._currentScenario?.data?.outcomes || {});
      this._playedScenarioIds.push(scenarioId);

      // empty linkedScenarioIds array
      this._linkedScenarioIds.splice(0);

      return true;
    } else {
      this._currentScenario = {};
      this._currentDocuments = {};
      this._outcomes = {};
    }

    // empty linkedScenarioIds array
    this._linkedScenarioIds.splice(0);
    return false;
  }

  parseOutcomesInt(data) {
    // parse outcomes to int
    const { option_a = {}, option_b = {} } = data;
    const { scores: scores_a = [] } = option_a;
    const { scores: scores_b = [] } = option_b;

    const optionA = {
      ...option_a,
      scores: [
        ...scores_a.map((data) => {
          return { ...data, score: parseInt(data.score) };
        }),
      ],
    };
    const optionB = {
      ...option_b,
      scores: [
        ...scores_b.map((data) => {
          return { ...data, score: parseInt(data.score) };
        }),
      ],
    };

    return { ...data, option_a: optionA, option_b: optionB };
  }

  retrieveScenarioById(id) {
    // get scenario by id
    return this._scenarioData.find((scenario) => {
      return scenario.id === id;
    });
  }

  getRandomScenarioId(data) {
    // pick a random id from data
    return Phaser.Math.RND.pick(data);
  }

  checkScenarioTimeRange(data) {
    const currentTime = this._game._player.currentTimeIndex;
    if (SCENARIO_REQUIREMENTS_METRICS[data.type].operator === OPERATOR_TYPES.LESS_THAN) {
      return currentTime <= parseInt(data.value);
    } else {
      return currentTime >= parseInt(data.value);
    }
  }

  checkScenarioScoreRange(data) {
    const scoreTypeData = SCENARIO_REQUIREMENTS_METRICS[data.type];

    const score = this._game._scores.getScoreByType(scoreTypeData.type);

    if (scoreTypeData.operator === OPERATOR_TYPES.LESS_THAN) {
      return score < parseInt(data.score);
    } else {
      return score > parseInt(data.score);
    }
  }

  getStartingScenario() {
    const startingScenarios = [];
    this._scenarioData.map((scenario) => {
      const startingScenario = scenario?.data?.starting_scenario || false;
      if (startingScenario === 'true') {
        startingScenarios.push(scenario.id);
      }
    });
    return startingScenarios.length > 0 ? startingScenarios : false;
  }

  getFilteredScenarios() {
    // created filtered scenarios array list, based on _scenarioData filtering out _playedScenarioIds and selecting by scoreData
    const filteredScenarios = this._scenarioData.filter((scenario) => {
      let requirementsMet = true;
      let hasRepeatableRequirement = false;
      if (scenario.data.requirements.length > 0) {
        for (let i = 0; i < scenario.data.requirements.length; i++) {
          const requirement = scenario.data.requirements[i];

          // Is scenario repeatable?
          // Is scenario repeatable even needed?
          if (requirement.type === SCENARIO_REQUIREMENTS_TYPES.REPEATABLE) {
            hasRepeatableRequirement = true;
            if (requirement.value == 'false') {
              requirementsMet = this._playedScenarioIds.indexOf(scenario.id) == -1 ? true : false;
            }

            if (!requirementsMet) {
              break;
            } else {
              continue;
            }
          }

          // If the requirement is a time based check
          if (
            requirement.type === SCENARIO_REQUIREMENTS_TYPES.TIME_LESS_THAN ||
            requirement.type === SCENARIO_REQUIREMENTS_TYPES.TIME_MORE_THAN
          ) {
            requirementsMet = this.checkScenarioTimeRange(requirement);

            if (!requirementsMet) {
              break;
            } else {
              continue;
            }
          }

          // If the requirement is a linked scenario check
          if (requirement.type === SCENARIO_REQUIREMENTS_TYPES.SCENARIO_CHOICE) {
            const playersChoice = this._game._player.getPlayersChoiceByScenarioId(
              requirement.scenario
            );

            requirementsMet =
              playersChoice &&
              playersChoice === `option_${requirement.choice}` &&
              this._playedScenarioIds.indexOf(scenario.id) == -1;

            if (requirementsMet) {
              this._linkedScenarioIds.push(scenario.id);
            }

            if (!requirementsMet) {
              break;
            } else {
              continue;
            }
          }

          // finially has to be a player score based check
          requirementsMet = this.checkScenarioScoreRange(requirement);

          if (!requirementsMet) {
            break;
          } else {
            continue;
          }
        }
      }
      // Is scenario repeatable even needed?
      if (!hasRepeatableRequirement && this._playedScenarioIds.indexOf(scenario.id) !== -1) {
        return false;
      }

      return requirementsMet;
    });

    // reduce the filteredScenarios array to a list of ids
    return filteredScenarios.reduce((acc, scenario) => {
      acc.push(scenario.id);
      return acc;
    }, []);
  }

  // -------------------------------------------
  // Functions for current scenario documents
  // -------------------------------------------

  getCurrentScenarioDocuments() {
    // get current scenario
    return this._currentDocuments;
  }

  displayScenarioDocument(id) {
    const data = { ...this._currentDocuments[id], doc_id: id } || {};
    const { name = '' } = data;
    this._game._player.setScenarioDocuments = {
      doc_name: name,
      scenario_id: this._game._scenario.getCurrentScenarioId,
    };

    this._game.sceneController.showOverlayScene(KEYS.ModalDocument, { data });
    this._readDocuments.push(id);
  }

  // -------------------------------------------
  // Functions for current scenario outcomes
  // -------------------------------------------

  setPlayersChoice(choice) {
    this._chosenOutcomes.push(this._outcomes[choice]);

    this._currentScenarioPlayersChoice = choice;
    this._game._player.updatePlayedScenarios = { id: this._currentScenario.id, choice };
  }

  resetPlayersChoice() {
    this._currentScenarioPlayersChoice = '';
  }

  // -------------------------------------------
  // Functions for current reseting scenario data
  // -------------------------------------------
  resetData() {
    this._playedScenarioIds = [];
    this._linkedScenarioIds = [];
    this._scenarioIds = [];
    this._chosenOutcomes = [];
    this._currentScenarioPlayersChoice = '';
    this._currentScenario = {};
    this._currentDocuments = {};
    this._readDocuments = [];
    this._outcomes = {};
  }
}
